import { FC, useCallback } from 'react';
import {
  ActionButton,
  confirmationHeaders,
  DESCRIPTION_MAX_LENGTH,
  DetailLayout,
  EditLock,
  Form,
  FormAlerts,
  FormField,
  FormInput,
  GetApiErrorMessage,
  InfoIconUrl,
  NAME_MAX_LENGTH,
  ObjectHeader,
  ObjectType,
  Padding,
  Stack,
  DataObjectGroupDto,
  DataObjectGroupValidFormData,
  useMutateDataObjectGroup,
  useMutationErrors,
  useAlertContext,
  DataObjectGroupValidFormDataSchema,
  useNavigationBlockers,
  CODE_MAX_LENGTH,
  FormRadioGroup,
  validationTranslations,
} from '@atlas-frontend/sdk';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import translations from './DataObjectGroupForm.translations.json';
import { DataObjectGroupBreadcrumbs } from './DataObjectGroupBreadcrumbs';
import { useWatch } from 'react-hook-form';
import { Flex } from 'antd';

const { addTranslations, useTranslation, t } = i18nNsFactory(
  'data-object-group-form',
);
addTranslations(translations);
addTranslations(validationTranslations);

const translateMutationError: GetApiErrorMessage = (code, meta) =>
  t('alert.error.code.' + code, meta?.args && { ...meta.args });

export type DataObjectGroupFormProps =
  | {
      mode: 'create';
      id?: never;
      defaultValues?: never;
      onClickCancel: () => void;
      onSuccess?: (response: DataObjectGroupDto) => void;
    }
  | {
      mode: 'edit';
      id: number;
      defaultValues: DataObjectGroupDto;
      onClickCancel: () => void;
      onSuccess?: (response: DataObjectGroupDto) => void;
    };

export const DataObjectGroupForm: FC<DataObjectGroupFormProps> = (props) => {
  const { t } = useTranslation();
  const { mode, id, defaultValues, onClickCancel, onSuccess } = props;
  const { mutateAsync } = useMutateDataObjectGroup();
  const { mutationErrors, setMutationErrors, clearMutationErrors } =
    useMutationErrors(translateMutationError);
  const { resetAlert, setSuccess } = useAlertContext();
  const { clear: clearBlockers } = useNavigationBlockers();

  const handleFinish = useCallback(
    (values: DataObjectGroupValidFormData, confirm: boolean) => {
      resetAlert();
      clearMutationErrors();
      return mutateAsync({
        id: mode === 'edit' ? id : undefined,
        data: values,
        headers: confirmationHeaders(confirm),
      })
        .then((responseData) => {
          if (responseData) {
            if (Object.hasOwn(responseData, 'id')) {
              clearBlockers();
              setSuccess(
                mode === 'create' ? t('alert.success.create') : undefined,
              );
              onSuccess?.(responseData);
            } else {
              throw new Error('Expecting "id" in response data.');
            }
          }
        })
        .catch((error) => {
          setMutationErrors(error.response?.data.errors ?? []);
          throw error;
        });
    },
    [
      resetAlert,
      clearMutationErrors,
      mutateAsync,
      mode,
      id,
      clearBlockers,
      setSuccess,
      t,
      onSuccess,
      setMutationErrors,
    ],
  );

  return (
    <Form
      schema={DataObjectGroupValidFormDataSchema}
      defaultValues={defaultValues}
      serverErrors={mutationErrors}
      onSubmit={handleFinish}
    >
      <DetailLayout
        infoIconUrl={InfoIconUrl.DATA_OBJECT_GROUP_DETAILS}
        breadCrumb={<DataObjectGroupBreadcrumbs data={defaultValues} />}
        side={{
          header: <DataObjectGroupHeader />,
          content: (
            <Padding>
              <Stack size="md">
                {mode === 'create' ? (
                  <FormField
                    name="groupType"
                    label={t('groupType.label')}
                    required
                  >
                    <FormRadioGroup
                      name="groupType"
                      options={[
                        { value: 'I', label: t('groupType.options.I') },
                        { value: 'O', label: t('groupType.options.O') },
                      ]}
                    />
                  </FormField>
                ) : (
                  <FormField
                    name="groupType"
                    label={t('groupType.label')}
                    preview={{
                      previewOnly: true,
                      style: { paddingLeft: 0 },
                      render: (value) => t('groupType.options.' + value),
                    }}
                  />
                )}
                <Flex gap="middle" justify="space-between">
                  <FormField
                    name="code"
                    label={t('code.label')}
                    maxLength={CODE_MAX_LENGTH}
                    required
                    flex={0.3}
                  />
                  <FormField
                    name="name"
                    label={t('name.label')}
                    maxLength={NAME_MAX_LENGTH}
                    required
                    flex={1}
                  />
                </Flex>
                <FormField name="description" label={t('description.label')}>
                  <FormInput
                    name="description"
                    rows={2}
                    autoSize
                    maxLength={DESCRIPTION_MAX_LENGTH}
                  />
                </FormField>
              </Stack>
            </Padding>
          ),
          footer:
            mode === 'edit' ? (
              <EditLock
                id={defaultValues.id}
                entity={defaultValues}
                objectType={ObjectType.DataObjectGroup}
                onTimeout={onClickCancel}
              />
            ) : undefined,
        }}
        headerConfig={{
          actions: (
            <>
              <ActionButton type="abort" onClick={onClickCancel} />
              <ActionButton type="formSubmit" />
            </>
          ),
        }}
        alert={<FormAlerts />}
      />
    </Form>
  );
};

const DataObjectGroupHeader = () => {
  const code = useWatch({ name: 'code' });
  const name = useWatch({ name: 'name' });

  return (
    <Padding>
      <ObjectHeader upperText={code} lowerText={name} />
    </Padding>
  );
};
